import React from 'react';
import './HighlightTooltip.css';
import BrandBadgeIcon from '../../../../assets/icons/icon_brand_badge.png';
import TiktokBadgeIcon from '../../../../assets/icons/tiktok-badge.png';
import InstagramBadgeIcon from '../../../../assets/icons/instagram-badge.png';
import Spinner from '../../../common/Spinner/Spinner';

interface HighlightUser {
  username: string;
  profile_picture: string;
  account_type: string;
  user_id?: number;
}

interface HighlightTooltipProps {
    users: HighlightUser[];
    onSelect: (
      text: string,
      type: 'mention' | 'hashtag',
      mentionType: 'user' | 'brand' | 'tiktok' | 'instagram' | '',
      user_id?: number,
      tag_id?: number
    ) => void;
    loading?: boolean;
    position?: {
      top: number;
      left: number;
    };
    searchText?: string;
    highlight_type: 'mention' | 'hashtag';
}

const HighlightTooltip: React.FC<HighlightTooltipProps> = ({
  users,
  onSelect,
  loading = false,
  position,
  searchText,
  highlight_type: highlight_type
}) => {
  const handleRowClick = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>, 
    user: HighlightUser
  ) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (user.username !== "Start typing...") {
      console.log('handleRowClick - selecting user:', user.username);
      onSelect(user.username, highlight_type, user.account_type as 'user' | 'brand' | '', user.user_id, undefined);
    }
  };

  const handleSocialOptionClick = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    searchText: string, 
    socialType: 'tiktok' | 'instagram'
  ) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(searchText, 'mention', socialType, undefined, undefined);
  };

  const renderSocialOptions = () => {
    if (!searchText || highlight_type !== 'mention') return null;
    
    return (
      <>
        <div className="highlight-row highlight-divider">
          <div className="highlight-info">
            <span className="highlight-text highlight-header">
              Or mention social account?
            </span>
          </div>
        </div>
        
        <div 
          className="highlight-row social-option"
          onMouseDown={(e) => {
            e.stopPropagation(); // Add this
            e.preventDefault(); // Add this
          }}
          onClick={(e) => handleSocialOptionClick(e, searchText, 'tiktok')}
          role="button"
          tabIndex={0}
          style={{ 
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '8px' // Add padding for better click area
          }}
        >
          <span className="highlight-text">
            {searchText}
            <img 
              src={TiktokBadgeIcon} 
              alt="TikTok" 
              className="highlight-social-badge"
              style={{ pointerEvents: 'none' }}
            />
          </span>
        </div>

        <div 
          className="highlight-row social-option"
          onMouseDown={(e) => {
            e.stopPropagation(); // Add this
            e.preventDefault(); // Add this
          }}
          onClick={(e) => handleSocialOptionClick(e, searchText, 'instagram')}
          role="button"
          tabIndex={0}
          style={{ 
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '8px' // Add padding for better click area
          }}
        >
          <span className="highlight-text">
            {searchText}
            <img 
              src={InstagramBadgeIcon} 
              alt="Instagram" 
              className="highlight-social-badge"
              style={{ pointerEvents: 'none' }}
            />
          </span>
        </div>
      </>
    );
  };

  const getPlaceholderText = () => {
    return highlight_type === 'mention' ? 'Start typing a username...' : 'Start typing a hashtag...';
  };

  return (
    <div 
      className="highlight-tooltip"
      style={{
        top: position ? `${position.top}px` : '0',
        left: position ? `${position.left}px` : '0',
        visibility: position ? 'visible' : 'hidden',
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {loading ? (
        <div className="highlight-loading">
          <Spinner size={20} color="#666" />
        </div>
      ) : (
        <>
          {users.map((user, index) => (
            <div 
              key={index}
              className="highlight-row"
              role="button"
              tabIndex={0}
              onClick={(e) => handleRowClick(e, user)}
              onTouchStart={(e) => handleRowClick(e, user)}
              style={{ 
                cursor: user.username === getPlaceholderText() ? 'default' : 'pointer',
                color: user.username === getPlaceholderText() ? '#999' : undefined,
                userSelect: 'none',
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {user.username !== getPlaceholderText() ? (
                <>
                  {highlight_type === 'mention' && (
                    <img 
                      src={user.profile_picture} 
                      alt={user.username}
                      className="highlight-avatar"
                    />
                  )}
                  <div className="highlight-info">
                    <span className="highlight-text">
                      {highlight_type === 'hashtag' ? '#' : ''}{user.username}
                      {highlight_type === 'mention' && user.account_type === 'brand' && (
                        <img 
                          src={BrandBadgeIcon} 
                          alt="Brand" 
                          className="highlight-brand-badge"
                        />
                      )}
                    </span>
                  </div>
                </>
              ) : (
                <div className="highlight-info">
                  <span className="highlight-text">{user.username}</span>
                </div>
              )}
            </div>
          ))}
          {renderSocialOptions()}
        </>
      )}
    </div>
  );
};

export default HighlightTooltip; 